import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { IMAGES, CONTACT } from "../../data/site";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i, className = "" }) => (
  <span className="mask-line">
    <motion.span
      className={`block ${className}`}
      variants={line}
      custom={i}
      initial="hidden"
      animate="show"
    >
      {children}
    </motion.span>
  </span>
);

const goTo = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  const lenis = window.__ssvpLenis;
  if (lenis && typeof lenis.scrollTo === "function") lenis.scrollTo(el, { offset: -72, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* warm glow behind the product (right side) */}
      <div className="absolute top-1/2 right-[-10%] lg:right-[6%] -translate-y-1/2 w-[85vw] h-[85vw] max-w-[760px] max-h-[760px] rounded-full bg-[#F59E0B] opacity-[0.16] blur-[120px] pointer-events-none z-0" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 min-h-[100svh] grid grid-cols-1 lg:grid-cols-2 items-center gap-6 lg:gap-10 pt-28 pb-28 lg:py-0">
        {/* LEFT — text */}
        <motion.div style={{ y: textY }} className="order-2 lg:order-1">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-marathi-bold font-bold text-[#F59E0B] text-base sm:text-xl mb-4 tracking-wide"
          >
            {CONTACT.taglineMarathi}
          </motion.p>

          <h1 className="font-display uppercase text-[#F8F4EC] leading-[0.85] text-6xl sm:text-7xl lg:text-8xl">
            <MaskLine i={0}>Pune’s</MaskLine>
            <MaskLine i={1} className="text-[#F59E0B]">favourite</MaskLine>
            <MaskLine i={2}>Vada Pav</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-7 max-w-md text-lg text-[#BFAFA6] leading-relaxed"
          >
            Hot, crispy and made to order — vada pav, poha, upma, shira &amp;
            cutting chai, served fresh in minutes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => goTo("#menu")}
              data-testid="hero-menu-cta"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#F59E0B] text-[#141110] font-semibold hover:bg-[#FBBF24] hover:shadow-[0_0_30px_rgba(245,158,11,0.45)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
            >
              Explore the Menu
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => goTo("#branches")}
              data-testid="hero-branches-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/20 text-[#F8F4EC] font-semibold hover:border-[#F59E0B] hover:text-[#F59E0B] transition-colors duration-300"
            >
              <MapPin size={17} /> Find a Branch
            </button>
          </motion.div>
        </motion.div>

        {/* RIGHT — product showcase */}
        <div className="order-1 lg:order-2 flex items-center justify-center">
          <motion.div style={{ y: imgY, scale: imgScale }} className="relative w-[82vw] max-w-[540px] lg:w-full">
            <motion.div
              animate={{ y: [0, -16, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <motion.img
                src={IMAGES.vadapav}
                alt="Signature Vada Pav"
                initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 1.3, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-auto drop-shadow-[0_40px_70px_rgba(0,0,0,0.6)]"
                data-testid="hero-product-image"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* bottom bar */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.4 }}
        className="absolute bottom-6 left-0 right-0 z-20 px-6 lg:px-10"
      >
        <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-white/10 pt-4">
          <p className="font-serif-e italic text-lg text-[#BFAFA6]">
            13 outlets across Pimpri-Chinchwad & Pune.
          </p>
          <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#F59E0B] font-bold">
            <span className="h-2 w-2 rounded-full bg-[#F59E0B] animate-pulse" />
            Open · 5 AM – 10 PM
          </div>
        </div>
      </motion.div>
    </section>
  );
};
